import {
  CALCULATE_WEIGHT_AND_BALANCE_ACTION,
  SET_WB_EVENT_CONTEXT,
  WB_SAVE_DONE,
  WEIGHT_AND_BALANCE,
} from './weight-and-balance.consts';

import { apiJSON, normalizeData } from '../../../configs/api-generators.js';
import { setMessages, setModal, setProgress } from '../../ui/ui.actions.js';
import { catchError, extractMessages, throwError } from '../../ui/ui.helpers';
import { CONFIRM_PASSENGER_MOVE } from './passenger/passenger.consts';
import { getEvent } from '../calendar-day.actions';

// Resolve a bare org slug from Redux URL state. `URL.eventParams.org` is the
// canonical bare slug parsed from the URL hash. `URL.org` is a path-prefix
// (`/org/{slug}`) used directly in template strings elsewhere — strip the
// prefix and trailing slashes before reusing it.
function getOrgUnique(URL) {
  const fromParams = URL?.eventParams?.org;
  if (fromParams) return String(fromParams);
  const prefix = URL?.org;
  if (typeof prefix === 'string') {
    const stripped = prefix.replace(/^\/?org\//, '').replace(/\/+$/, '');
    if (stripped) return stripped;
  }
  return '';
}

export function* getWeightAndBalance(): Yield<*> {
  try {
    yield setProgress(true, WEIGHT_AND_BALANCE);

    const { URL } = yield s => s;
    const eventId = URL.eventParams.event;
    const orgUnique = getOrgUnique(URL);

    if (!eventId || !orgUnique) {
      return setProgress(false, WEIGHT_AND_BALANCE);
    }

    const response = yield* apiJSON(
      `/org/${orgUnique}/calendar/getWeightAndBalance/${eventId}/`,
      {}
    );

    if (response.ok) {
      yield {
        type: CALCULATE_WEIGHT_AND_BALANCE_ACTION,
        payload: { ...response.data, eventId, warning: response.warning }
      };
      yield setMessages(extractMessages(response, WEIGHT_AND_BALANCE, 5000));
      return setProgress(false, WEIGHT_AND_BALANCE);
    }

    throwError(response);
  } catch (e) {
    yield setProgress(false, WEIGHT_AND_BALANCE);
    return catchError(e, WEIGHT_AND_BALANCE);
  }
}

export function* saveWeightAndBalance(requestBody): Yield<*> {
  try {
    yield setProgress(true, WEIGHT_AND_BALANCE);

    const { URL, timezone } = yield s => s;
    const orgUnique = getOrgUnique(URL);
    if (!orgUnique) return setProgress(false, WEIGHT_AND_BALANCE);

    const response = yield* apiJSON(
      `/org/${orgUnique}/calendar/saveWeightAndBalance/${URL.eventParams.event}/`,
      requestBody
    );

    if (response.ok) {
      yield {
        type: CALCULATE_WEIGHT_AND_BALANCE_ACTION,
        payload: requestBody
      };
      // Day-view: timezone is an object `{id, offset, …}`; legacy getEvent() uses timezone.id.
      // Month-view: timezone is a plain string — legacy getEvent() would corrupt state.
      // Use WB_SAVE_DONE as a React-side refresh signal instead.
      if (timezone && typeof timezone === 'object' && typeof timezone.id === 'number') {
        yield* getEvent();
      } else {
        yield { type: WB_SAVE_DONE, payload: { eventId: URL.eventParams.event } };
      }
      yield setMessages(extractMessages(response, WEIGHT_AND_BALANCE, 5000));
      return setProgress(false, WEIGHT_AND_BALANCE);
    }

    throwError(response);
  } catch (e) {
    yield setProgress(false, WEIGHT_AND_BALANCE);
    return catchError(e, WEIGHT_AND_BALANCE);
  }
}

export function* calculateWeightAndBalance(requestBody: {
  unit: 'lbs' | 'kg' | 'liters' | "%" | "gal",
  rows: Array<{ weight: number }>
}): Yield<*> {
  try {
    yield setProgress(true, WEIGHT_AND_BALANCE);

    const { URL } = yield s => s;
    const orgUnique = getOrgUnique(URL);
    if (!orgUnique) return setProgress(false, WEIGHT_AND_BALANCE);

    const response = yield* apiJSON(
      `/org/${orgUnique}/calendar/passengersWeightAndBalance/${URL.eventParams.event}/`,
      requestBody
    );

    if (response.ok) {
      yield {
        type: CALCULATE_WEIGHT_AND_BALANCE_ACTION,
        payload: {
          ...response.data,
          warning: response.warning
        }
      };
      yield setMessages(extractMessages(response, WEIGHT_AND_BALANCE, 5000));
      return setProgress(false, WEIGHT_AND_BALANCE);
    }

    throwError(response);
  } catch (e) {
    yield setProgress(false, WEIGHT_AND_BALANCE);
    return catchError(e, WEIGHT_AND_BALANCE);
  }
}

export function* getEventsForPassengerMove(requestBody): Yield<*> {
  try {
    yield setProgress(true, WEIGHT_AND_BALANCE);

    const { URL } = yield s => s;
    const orgUnique = getOrgUnique(URL);
    if (!orgUnique) return setProgress(false, WEIGHT_AND_BALANCE);

    const response = yield* apiJSON(
      `/org/${orgUnique}/calendar/eventsForPassengerMove/${URL.eventParams.event}/`,
      requestBody
    );

    if (response.ok) {
      yield setProgress(false, WEIGHT_AND_BALANCE);

      const { resources, resourcesList } = normalizeData(
        response.data.resources,
        'resources',
        'resourcesList',
        'resourceID'
      );

      return {
        ...response.data,
        resources,
        resourcesList,
        daysList: Object.keys(response.data.days)
      };
    }

    throwError(response);
  } catch (e) {
    yield setProgress(false, WEIGHT_AND_BALANCE);
    return catchError(e, WEIGHT_AND_BALANCE);
  }
}

export function* movePassenger(requestBody): Yield<*> {
  try {
    yield setProgress(true, WEIGHT_AND_BALANCE);

    const { URL } = yield s => s;
    const orgUnique = getOrgUnique(URL);
    if (!orgUnique) return setProgress(false, WEIGHT_AND_BALANCE);

    const response = yield* apiJSON(
      `/org/${orgUnique}/calendar/movePassenger/${URL.eventParams.event}/`,
      requestBody
    );

    if (response.ok) {
      yield setProgress(false, WEIGHT_AND_BALANCE);
      yield setModal(false, CONFIRM_PASSENGER_MOVE);
      yield setMessages(extractMessages(response, WEIGHT_AND_BALANCE, 5000));
      yield* getEvent(requestBody.newEvent);
      yield* getEvent();
      return response;
    }

    throwError(response);
  } catch (e) {
    yield setProgress(false, WEIGHT_AND_BALANCE);
    return catchError(e, WEIGHT_AND_BALANCE);
  }
}

/**
 * Seeds the wbEventContext slice from an event's eventResources array so the
 * W&B component can resolve aircraftId + pilotRight without needing
 * `resource.events[]` (which the month-view refs bridge does not populate).
 * Called by EventViewModal before mounting the W&B screen.
 *
 * @param {string|number} eventId
 * @param {Array} eventResources  — raw Symfony eventResources from the event detail
 */
export function* seedWbEventContext(eventId, eventResources): Yield<*> {
  if (!eventId || !Array.isArray(eventResources) || eventResources.length === 0) {
    return { type: SET_WB_EVENT_CONTEXT, payload: null };
  }

  let aircraftId = null;
  let pilotRight = null;

  for (const er of eventResources) {
    // Support all Symfony serialization shapes (per EventViewModal pickAircraft)
    const aircraft =
      er?.aircraft ||
      er?.resource?.aircraft ||
      (er?.resource && String(er.resource?.type) === 'aircraft' ? er.resource : null);

    if (aircraft) {
      aircraftId = aircraft.id ?? null;
      // pilotRight lives on the EventResource row (er), not on the aircraft entity
      const pr = er.pilotRight ?? er.resource?.pilotRight ?? null;
      pilotRight = pr === true || pr === 1 || pr === '1';
      break;
    }
  }

  return {
    type: SET_WB_EVENT_CONTEXT,
    payload: aircraftId != null ? { eventId: String(eventId), aircraftId, pilotRight } : null,
  };
}
